import { AxiosError } from "axios";
import axios from "axios";

import debugLogger from "./debugLogger";

export enum ErrorSeverity {
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

export enum ErrorSource {
  API = "api",
  AUTH = "auth",
  VALIDATION = "validation",
  NETWORK = "network",
  APPLICATION = "application",
}

export interface AppError {
  message: string;
  code?: string;
  severity: ErrorSeverity;
  source: ErrorSource;
  originalError?: unknown;
  metadata?: Record<string, unknown>;
}

export interface ErrorResponse {
  error?: string;
  message?: string;
  statusCode?: number;
  errors?: Record<string, string[]>;
}

/**
 * Creates a standardized application error
 */
export function createAppError(
  message: string,
  source: ErrorSource = ErrorSource.APPLICATION,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  originalError?: unknown,
  metadata?: Record<string, unknown>,
  code?: string,
): AppError {
  return {
    message,
    severity,
    source,
    originalError,
    metadata,
    code,
  };
}

/**
 * Extracts a readable error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;

      // Handle validation errors
      if (axiosError.response?.data?.errors) {
        const errors = Object.values(axiosError.response.data.errors).flat();
        return errors[0] || "Validation error occurred";
      }

      // Handle standard API errors
      if (axiosError.response?.data?.error) {
        return axiosError.response.data.error;
      }

      // Use server-provided message before falling back to generic status messages
      if (axiosError.response?.data?.message) {
        return axiosError.response.data.message;
      }

      // Handle network errors
      if (axiosError.code === "ECONNABORTED") {
        return "Request timeout. Please try again.";
      }

      if (!axiosError.response) {
        return "Network error. Please check your connection.";
      }

      // Handle common HTTP status codes
      switch (axiosError.response.status) {
        case 401:
          return "Authentication failed. Please check your credentials and try again.";
        case 403:
          return "You do not have permission to perform this action.";
        case 404:
          return "The requested resource was not found.";
        case 422:
          return "Invalid data provided.";
        case 429:
          return "Too many requests. Please try again later.";
        case 500:
          return "An internal server error occurred. Please try again later.";
        default:
          return axiosError.message || "An unexpected error occurred";
      }
    }
    return error.message;
  }
  return "An unexpected error occurred";
}

/**
 * Transforms any error into a standardized AppError
 */
export function normalizeError(error: unknown): AppError {
  // If it's already an AppError, return it
  if (
    typeof error === "object" &&
    error !== null &&
    "source" in error &&
    "severity" in error
  ) {
    return error as AppError;
  }

  const message = getErrorMessage(error);

  // Determine error source based on error type
  let source = ErrorSource.APPLICATION;
  let severity = ErrorSeverity.ERROR;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;

    if (!axiosError.response) {
      source = ErrorSource.NETWORK;
    } else if (
      axiosError.response.status === 401 ||
      axiosError.response.status === 403
    ) {
      source = ErrorSource.AUTH;
    } else if (
      axiosError.response.status === 422 ||
      (axiosError.response.data && "errors" in axiosError.response.data)
    ) {
      source = ErrorSource.VALIDATION;
      severity = ErrorSeverity.WARNING;
    } else {
      source = ErrorSource.API;
    }
  }

  return createAppError(message, source, severity, error);
}

/**
 * Logs an error with appropriate level based on severity
 */
export function logError(error: unknown): void {
  const appError = normalizeError(error);

  const logData = {
    message: appError.message,
    source: appError.source,
    metadata: appError.metadata,
    originalError: appError.originalError,
  };

  switch (appError.severity) {
    case ErrorSeverity.ERROR:
      debugLogger.error("[ERROR]", logData);
      break;
    case ErrorSeverity.WARNING:
      debugLogger.warn("[WARNING]", logData);
      break;
    case ErrorSeverity.INFO:
      debugLogger.info("[INFO]", logData);
      break;
  }
}
